import {
  getTagName,
  tTagEntry,
} from '../Chat/components/ChatInfo/components/ChatInfoAttributes/ChatInfoAttributes.tag.utils';
import { tMessageSearchResult } from '../../pages/MessengerV2/MessageSearch.types';
import { tAdvanceSearchLead } from './AdvanceSearchModal.types';

/**
 * Coerces a heterogeneous tag array into the string[] shape expected by
 * tAdvanceSearchLead.tags. Accepts the three shapes that currently reach the
 * frontend: plain strings, {name} objects (legacy socket payload), and
 * {id, name, color} objects (current TagTransformer output from leads-service).
 * Empty names are dropped so consumers never render a blank pill.
 *
 * @param tags - Raw tag array from a list/lead endpoint or socket payload.
 * @returns Array of tag display names; never null/undefined.
 */
export function normalizeLeadTags(
  tags: tTagEntry[] | null | undefined,
): string[] {
  if (!tags) return [];
  return tags.map(getTagName).filter((name) => name.length > 0);
}

/**
 * Adapter for upstream list/search/messenger components that hold a lead
 * entity with a richer tag shape ({id, name, color}[]) and need to hand it
 * off to the Chat modal, whose state contract declares tags as string[].
 *
 * Centralising the adaptation here is intentional: the type assertion is
 * unavoidable given the cross-slice shape drift, but it is confined to one
 * tested function instead of being scattered across seven call sites. The
 * single permitted `as unknown as tAdvanceSearchLead` below carries an inline
 * eslint-disable so the ESLint rule banning that pattern stays active for the
 * rest of the file and for any future no-restricted-syntax rules.
 *
 * Why the generic is unconstrained: the upstream list-lead types (tFunnelLead,
 * tChatLead, and peers) do not declare a `tags` field in TypeScript, even
 * though the runtime payload has one. Constraining the generic to
 * `extends { tags?: tTagEntry[] | null }` trips TypeScript's weak-type check
 * for every call site whose source type does not declare tags, and propagating
 * a `tags?` declaration into those types is a cross-module contract change
 * out of scope for this adapter. The runtime contract is instead guarded by
 * the unit tests in this module's test file, which cover null / undefined /
 * string[] / object[] / mixed-array inputs. Tightening the type is tracked
 * against the Phase 2 backend alignment work (see the plan in docs/plans/).
 *
 * @param listLead - Lead entity from a list/search response. The runtime
 *                   payload may carry an optional tags field; any other
 *                   shape difference from tAdvanceSearchLead is passed
 *                   through unchanged.
 * @returns A tAdvanceSearchLead with tags normalised to string[].
 */
export function toChatModalLead<TListLead>(
  listLead: TListLead,
): tAdvanceSearchLead {
  const sourceTags = (listLead as { tags?: tTagEntry[] | null }).tags;
  return {
    // eslint-disable-next-line no-restricted-syntax -- single permitted double-cast; the whole point of this adapter.
    ...(listLead as unknown as tAdvanceSearchLead),
    tags: normalizeLeadTags(sourceTags),
  };
}

/**
 * Adapter for a message-search hit (SRE-3328) on its way to the Chat modal.
 *
 * The search endpoint answers with `chatId`, but the front has no `?chatId=`
 * route and no reverse chatId -> lead lookup: every conversation is opened by
 * `leadId + handle + integration`, which is exactly why those three fields
 * were added to the response. This builds the smallest lead the Chat modal
 * accepts out of them and hands it to `toChatModalLead`, so the single
 * permitted `as unknown as tAdvanceSearchLead` stays in that one tested place
 * and the `no-restricted-syntax` ban holds here.
 *
 * Nothing else about the lead is invented: the name, funnel, stage and status
 * are not in a search hit, and Chat.hook re-fetches the lead by id on open and
 * merges the real values in. Sending a fabricated name would render it for the
 * moment before that fetch lands.
 *
 * @param result - One row of `GET /workspace/:wid/messages/search`.
 * @returns A modal-shape lead carrying the id, handle and channel to open.
 */
export function toMessageSearchChatModalLead(
  result: Pick<tMessageSearchResult, 'leadId' | 'handle' | 'channel'>,
): tAdvanceSearchLead {
  return toChatModalLead({
    id: result.leadId,
    handle: result.handle,
    chat: { integration: result.channel },
    tags: [],
  });
}
